import type {ReactElement} from 'react';
import React, {useState} from 'react';
import {PosterEditingBaseFontSelector} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-base-font-selector';
import type {BrandFont} from '@Components/editor-brands/editor-brands.types';
import type {FontData} from '@Libraries/font-library';
import {isInternalFont, USER_FONT_LICENSE} from '@Libraries/font-library';
import {onAddBrandAssetClickedHandler} from '@Components/poster-editor/library/poster-editor-library';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';
import {useGetKeyFromTextStylesOfSelectedTextSlideItem} from '@Hooks/poster-editor/useGetKeyFromTextStylesOfSelectedTextSlideItem';

interface TextSlideFontSelectorProps {
  onBack?(): void;
  onClose?(): void;
  showCloseButton?: boolean;
  showBackButton?: boolean;
  isForMobile?: boolean;
}

export function TextSlideFontSelector({
  isForMobile = false,
  onClose = (): void => {},
  onBack = (): void => {},
  showCloseButton = false,
  showBackButton = true,
}: TextSlideFontSelectorProps): ReactElement<any> | null {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();
  const activeSlide = activeSlideUID && activeItem?.isSlideshow() ? activeItem.getSelectedSlide() : null;
  const fontFamily = useGetKeyFromTextStylesOfSelectedTextSlideItem('fontFamily');
  const fontLicense = useGetKeyFromTextStylesOfSelectedTextSlideItem('fontLicense');
  const script = useGetKeyFromTextStylesOfSelectedTextSlideItem('script');
  const [temporaryState, setTemporaryState] = useState<string>('');
  const [isNewFontLoading, setIsNewFontLoading] = useState(false);

  if (!activeItem?.isSlideshow()) {
    throw new Error(`expected slideshow item instead got ${activeItem?.gitype}`);
  }

  if (!activeSlide || !activeSlideUID) {
    throw new Error('unable to get the selected text slide');
  }

  if (!('textStyles' in activeSlide)) {
    throw new Error(`Text Slide doesn't have textStyles property`);
  }

  if (fontFamily === undefined || fontLicense === undefined) {
    throw new Error('fontFamily attribute does not exist in the selected text slide');
  }

  const onSelectFontFamily = (fontFamily: string): void => {
    setIsNewFontLoading(true);
    setTemporaryState(fontFamily);
    const fontLicense = isInternalFont(fontFamily) ? '' : USER_FONT_LICENSE;
    const oldFontFamily = fontFamily;
    const oldFontLicense = fontLicense;
    activeItem
      .updateSlideFromObject(activeSlideUID, {
        textStyles: {fontFamily, fontLicense},
      })
      .then((): void => {})
      .catch((): void => {
        activeSlide.textStyles.fontFamily = oldFontFamily;
        activeSlide.textStyles.fontLicense = oldFontLicense;
        setTemporaryState(activeSlide.textStyles.fontFamily);
      })
      .finally((): void => {
        setIsNewFontLoading(false);
      });
  };

  return (
    <PosterEditingBaseFontSelector
      fontsInDesign={window.posterEditor?.whiteboard?.getFontsOnPoster() ?? []}
      onBrandItemClicked={(brand: BrandFont): void => {
        onSelectFontFamily(brand.fontFamily);
      }}
      isSelectedFontLoading={isNewFontLoading}
      selectedFontFamily={isNewFontLoading ? temporaryState : fontFamily}
      onSelectFontFamily={(font: FontData): void => {
        onSelectFontFamily(font.fontFamily);
      }}
      showHeader
      onBackButtonCallback={(): void => {
        onBack();
      }}
      onAddBrandAsset={(): void => {
        void onAddBrandAssetClickedHandler();
      }}
      scriptForRecommendedFonts={script}
      onCloseButtonCallback={(): void => {
        onClose();
      }}
      showCloseButton={showCloseButton}
      showBackButton={showBackButton}
      hasSmallTopPadding={isForMobile}
    />
  );
}
